import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Button Primitive - Base button component with variants
 *
 * Uses class-variance-authority for variant management and
 * Radix UI Slot for polymorphic rendering (asChild pattern).
 *
 * @see docs/architecture/OwnYou_architecture_v13.md Section 4.4
 */
import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../utils/cn';
const buttonVariants = cva(
// Base styles
'inline-flex items-center justify-center font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50', {
    variants: {
        variant: {
            primary: 'bg-brand-600 text-white hover:bg-brand-700',
            secondary: 'bg-brand-100 text-brand-700 hover:bg-brand-200',
            outline: 'border border-neutral-300 bg-transparent hover:bg-neutral-100',
            ghost: 'hover:bg-neutral-100',
            link: 'underline-offset-4 hover:underline text-brand-600',
        },
        size: {
            sm: 'h-8 px-3 text-sm rounded-md',
            md: 'h-10 px-4 text-base rounded-md',
            lg: 'h-12 px-6 text-lg rounded-md',
            icon: 'h-10 w-10 rounded-md',
        },
    },
    defaultVariants: {
        variant: 'primary',
        size: 'md',
    },
});
/**
 * Button primitive with variants
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md">Click me</Button>
 *
 * // As a link
 * <Button asChild>
 *   <a href="/contact">Contact</a>
 * </Button>
 * ```
 */
export const Button = forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (_jsx(Comp, { className: cn(buttonVariants({ variant, size, className })), ref: ref, ...props }));
});
Button.displayName = 'Button';
export { buttonVariants };
//# sourceMappingURL=Button.js.map